import {
  Axis,
  ChartOptions,
  merge,
  Options,
  TooltipOptions,
  XAxisOptions,
  YAxisOptions,
} from 'highcharts';
import moment from 'moment';
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  ProjectFinancialsContractType,
  ProjectFinancialsZoomLevel,
} from 'api/project';
import { ChartTooltipRenderer } from '../../components/common';
import {
  ProjectFinancialsChartTooltipProps,
  ProjectFinancialsComponentGroup,
  ProjectFinancialsGaugeValueRange,
  ProjectFinancialsTypeBarChartColorMap,
  ProjectFinancialsTypeGaugeChartColorMap,
  ProjectFinancialsTypeLineChartColorMap,
} from '../../types';

export const ProjectFinancialsGaugeChartColorMap: ProjectFinancialsTypeGaugeChartColorMap =
  {
    [ProjectFinancialsContractType.TM]: {
      [ProjectFinancialsComponentGroup.Budget]: {
        [ProjectFinancialsGaugeValueRange.InRange]: 'var(--blue-50)',
        [ProjectFinancialsGaugeValueRange.OutOfRange]: 'var(--red-50)',
      },
      [ProjectFinancialsComponentGroup.Hours]: {
        [ProjectFinancialsGaugeValueRange.InRange]: 'var(--blue-50)',
        [ProjectFinancialsGaugeValueRange.OutOfRange]: 'var(--red-50)',
      },
    },
    [ProjectFinancialsContractType.FIXED]: {
      [ProjectFinancialsComponentGroup.Budget]: {
        [ProjectFinancialsGaugeValueRange.InRange]: 'var(--purple-50)',
        [ProjectFinancialsGaugeValueRange.OutOfRange]: 'var(--red-50)',
      },
      [ProjectFinancialsComponentGroup.Hours]: {
        [ProjectFinancialsGaugeValueRange.InRange]: 'var(--blue-50)',
        [ProjectFinancialsGaugeValueRange.OutOfRange]: 'var(--red-50)',
      },
    },
    [ProjectFinancialsContractType.SUBSCRIPTION]: {
      [ProjectFinancialsComponentGroup.Budget]: {
        [ProjectFinancialsGaugeValueRange.InRange]: 'var(--purple-50)',
        [ProjectFinancialsGaugeValueRange.OutOfRange]: 'var(--red-50)',
      },
      [ProjectFinancialsComponentGroup.Hours]: {
        [ProjectFinancialsGaugeValueRange.InRange]: 'var(--blue-50)',
        [ProjectFinancialsGaugeValueRange.OutOfRange]: 'var(--red-50)',
      },
    },
    [ProjectFinancialsContractType.NONBILLABLE]: {
      [ProjectFinancialsComponentGroup.Budget]: {
        [ProjectFinancialsGaugeValueRange.InRange]: '',
        [ProjectFinancialsGaugeValueRange.OutOfRange]: 'var(--red-50)',
      },
      [ProjectFinancialsComponentGroup.Hours]: {
        [ProjectFinancialsGaugeValueRange.InRange]: '',
        [ProjectFinancialsGaugeValueRange.OutOfRange]: 'var(--red-50)',
      },
    },
  };

export const ProjectFinancialsLineChartColorMap: ProjectFinancialsTypeLineChartColorMap =
  {
    [ProjectFinancialsContractType.TM]: {
      projectBudget: 'var(--yellow-60)',
      estimatedRevenue: 'var(--blue-30)',
      actualRevenue: 'var(--blue-50)',
      estimatedCost: 'var(--magenta-30)',
      actualCost: 'var(--magenta-50)',
      estimatedProfit: 'var(--green-30)',
      actualProfit: 'var(--green-40)',
      estimatedMargin: 'var(--teal-60)',
      actualMargin: 'var(--teal-80)',
      projectHours: 'var(--yellow-60)',
      timeAllocated: 'var(--blue-30)',
      timeTracked: 'var(--blue-50)',
      billableTimeTracked: 'var(--green-40)',
      nonBillableTimeTracked: 'var(--blue-50)',
    },
    [ProjectFinancialsContractType.FIXED]: {
      projectBudget: 'var(--blue-50)',
      estimatedRevenue: 'var(--purple-30)',
      actualRevenue: 'var(--purple-50)',
      estimatedCost: 'var(--magenta-30)',
      actualCost: 'var(--magenta-50)',
      estimatedProfit: 'var(--green-30)',
      actualProfit: 'var(--green-40)',
      estimatedMargin: 'var(--teal-60)',
      actualMargin: 'var(--teal-80)',
      projectHours: 'var(--yellow-60)',
      timeAllocated: 'var(--blue-30)',
      timeTracked: 'var(--blue-50)',
      billableTimeTracked: 'var(--green-40)',
      nonBillableTimeTracked: 'var(--blue-50)',
    },
    [ProjectFinancialsContractType.SUBSCRIPTION]: {
      projectBudget: 'var(--yellow-60)',
      estimatedRevenue: 'var(--blue-30)',
      actualRevenue: 'var(--blue-50)',
      estimatedCost: 'var(--magenta-30)',
      actualCost: 'var(--magenta-50)',
      estimatedProfit: 'var(--green-30)',
      actualProfit: 'var(--green-40)',
      estimatedMargin: 'var(--teal-60)',
      actualMargin: 'var(--teal-80)',
      projectHours: 'var(--yellow-60)',
      timeAllocated: 'var(--blue-30)',
      timeTracked: 'var(--blue-50)',
      billableTimeTracked: 'var(--green-40)',
      nonBillableTimeTracked: 'var(--blue-50)',
    },
    [ProjectFinancialsContractType.NONBILLABLE]: {
      projectBudget: 'var(--yellow-60)',
      estimatedRevenue: 'var(--blue-30)',
      actualRevenue: 'var(--blue-50)',
      estimatedCost: 'var(--magenta-30)',
      actualCost: 'var(--magenta-50)',
      estimatedProfit: 'var(--green-30)',
      actualProfit: 'var(--green-40)',
      estimatedMargin: 'var(--teal-60)',
      actualMargin: 'var(--teal-80)',
      projectHours: 'var(--yellow-60)',
      timeAllocated: 'var(--blue-30)',
      timeTracked: 'var(--blue-50)',
      billableTimeTracked: 'var(--green-40)',
      nonBillableTimeTracked: 'var(--blue-50)',
    },
  };

export const ProjectFinancialsBarChartColorMap: ProjectFinancialsTypeBarChartColorMap =
  {
    [ProjectFinancialsContractType.TM]: {
      estimatedRevenue: 'var(--blue-30)',
      actualRevenue: 'var(--blue-50)',
      margin: 'var(--teal-80)',
      estimatedCost: 'var(--magenta-30)',
      actualCost: 'var(--magenta-50)',
      timeAllocated: 'var(--blue-30)',
      billableTimeTracked: 'var(--blue-50)',
      nonBillableTimeTracked: 'var(--cyan-30)',
    },
    [ProjectFinancialsContractType.FIXED]: {
      estimatedRevenue: 'var(--purple-30)',
      actualRevenue: 'var(--purple-50)',
      margin: 'var(--teal-80)',
      estimatedCost: 'var(--magenta-30)',
      actualCost: 'var(--magenta-50)',
      timeAllocated: 'var(--blue-30)',
      billableTimeTracked: 'var(--blue-50)',
      nonBillableTimeTracked: 'var(--cyan-30)',
    },
    [ProjectFinancialsContractType.SUBSCRIPTION]: {
      estimatedRevenue: 'var(--blue-53)',
      actualRevenue: 'var(--blue-50)',
      margin: 'var(--teal-80)',
      estimatedCost: 'var(--magenta-30)',
      actualCost: 'var(--magenta-50)',
      timeAllocated: 'var(--blue-30)',
      billableTimeTracked: 'var(--blue-50)',
      nonBillableTimeTracked: 'var(--cyan-30)',
    },
    [ProjectFinancialsContractType.NONBILLABLE]: {
      estimatedRevenue: 'var(--blue-30)',
      actualRevenue: 'var(--blue-50)',
      margin: 'var(--teal-80)',
      estimatedCost: 'var(--magenta-30)',
      actualCost: 'var(--magenta-50)',
      timeAllocated: 'var(--blue-30)',
      billableTimeTracked: 'var(--blue-50)',
      nonBillableTimeTracked: 'var(--cyan-30)',
    },
  };

export const setDateTimeChartExtremes = (
  xAxis: Axis,
  xAxisDates: number[],
  visibleDataCount: number,
  chartZoomLevel: ProjectFinancialsZoomLevel,
) => {
  const { min, max, dataMin, dataMax } = xAxis.getExtremes();

  let correctedMin;
  let correctedMax;

  if (xAxisDates.length < 6) {
    correctedMin = dataMin;
    correctedMax = dataMax;
  } else {
    const minDate = moment(min)
      .startOf(
        chartZoomLevel === ProjectFinancialsZoomLevel.Quarterly
          ? 'quarter'
          : 'month',
      )
      .startOf('day')
      .valueOf();
    const maxDate = moment(max)
      .startOf(
        chartZoomLevel === ProjectFinancialsZoomLevel.Quarterly
          ? 'quarter'
          : 'month',
      )
      .startOf('day')
      .valueOf();

    correctedMin = minDate;
    correctedMax = maxDate;

    const dataMinIndex = xAxisDates.indexOf(dataMin);
    const dataMaxIndex = xAxisDates.indexOf(dataMax);

    if (minDate <= dataMin) {
      correctedMin = dataMin;
      correctedMax = xAxisDates[dataMinIndex + visibleDataCount - 1];
    } else if (maxDate >= dataMax) {
      correctedMin = xAxisDates[dataMaxIndex - visibleDataCount + 1];
      correctedMax = dataMax;
    }
  }

  xAxis.setExtremes(correctedMin, correctedMax);
};

export const projectFinancialsBaseXAxisOptions = (
  customXAxisOptions?: Partial<XAxisOptions>,
): XAxisOptions =>
  merge(
    {
      type: 'datetime',
      tickWidth: 0,
      tickmarkPlacement: 'on',
      lineColor: 'var(--gray-20)',
    },
    customXAxisOptions,
  );

export const projectFinancialsBaseYAxisOptions = (
  customYAxisOptions?: Partial<YAxisOptions>,
): YAxisOptions =>
  merge(
    {
      title: {
        text: undefined,
      },
      allowDecimals: false,
      lineWidth: 1,
      lineColor: 'var(--gray-20)',
      gridLineWidth: 0,
    },
    customYAxisOptions,
  );

interface BaseTooltipProps
  extends Omit<ProjectFinancialsChartTooltipProps, 'x' | 'points'> {
  type: string;
  showX: boolean;
}

export const projectFinancialsBaseChartTooltipOptions = ({
  type,
  showX,
  currencyCode,
  revenueLabels,
  timeLabels,
  percentageLabels,
  maxValue,
  maxLabel,
  remainingValue,
  remainingLabel,
}: BaseTooltipProps): TooltipOptions => ({
  formatter() {
    return renderToStaticMarkup(
      <ChartTooltipRenderer
        x={showX ? this.x : undefined}
        points={type === 'solidgauge' ? [this] : this.points ?? []}
        currencyCode={currencyCode}
        revenueLabels={revenueLabels}
        timeLabels={timeLabels}
        percentageLabels={percentageLabels}
        maxValue={maxValue}
        maxLabel={maxLabel}
        remainingValue={remainingValue}
        remainingLabel={remainingLabel}
      />,
    );
  },
  shared: true,
  borderWidth: 0,
  backgroundColor: 'transparent',
  shadow: false,
  hideDelay: 200,
  useHTML: true,
});

interface BaseChartProps {
  chartOptions: ChartOptions;
  tooltipProps: BaseTooltipProps;
}

export const projectFinancialsBaseChartOptions = ({
  chartOptions,
  tooltipProps,
}: BaseChartProps): Options => ({
  title: {
    text: undefined,
  },
  credits: {
    enabled: false,
  },
  chart: merge(
    {
      events: {
        load: (e: any) => {
          e?.target.reflow();
        },
      },
      style: {
        fontFamily: 'var(--font-family)',
      },
    },
    chartOptions,
  ),
  time: {
    useUTC: false,
  },
  legend: {
    layout: 'horizontal',
    align: 'right',
    verticalAlign: 'top',
    margin: 40,
    useHTML: true,
    itemStyle: {
      fontWeight: 'var(--font-weight-normal)',
    },
  },
  scrollbar: {
    enabled: false,
  },
  tooltip: projectFinancialsBaseChartTooltipOptions(tooltipProps),
});
